import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import MetricDisplay from '../shared/metrics/MetricDisplay';
import { KpiGrid } from '../shared/metrics/styles';
import operationalMetricService from '../../services/operationalMetricService';
import {
    filterCatalogByRoles,
    intersectWithApiTypes,
    isTransportCompanyDashboardPreset,
} from '../../constants/operationalMetricCatalog';
import {
    canLoadOperationalDashboard,
    groupMetricsByDashboardSection,
    resolveDashboardCustomerId,
} from '../../utils/operationalMetricUtils';
import {
    getDefaultDeliveryDateRange,
    validateDeliveryDateRange,
} from '../../utils/deliveryDateRange';
import { FiltersContainer, FilterGroup } from '../shared/Crud/styles';
import CollapsibleSection from '../shared/CollapsibleSection';

const PanelStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: clamp(16px, 3vw, 24px);
    min-width: 0;
`;

const FilterError = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors?.danger?.main || '#dc2626'};
`;

const LoadError = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors?.danger?.main || '#dc2626'};
`;

function MetricsLayout({ layout, loading, kpiLabel }) {
    const charts = [
        ...layout.seriesMetrics,
        ...layout.statusMetrics,
        ...layout.rankingMetrics,
        ...layout.unknown,
    ];

    return (
        <>
            {layout.kpis.length > 0 ? (
                <KpiGrid aria-label={kpiLabel}>
                    {layout.kpis.map((metric) => (
                        <MetricDisplay
                            key={`${metric.key}-${metric.layoutSlot || 'kpi'}`}
                            metric={metric}
                            loading={loading}
                            compactKpi
                            hideHeader
                        />
                    ))}
                </KpiGrid>
            ) : null}
            {charts.map((metric) => (
                <MetricDisplay
                    key={`${metric.key}-${metric.layoutSlot || metric.valueType || 'metric'}`}
                    metric={metric}
                    loading={loading}
                />
            ))}
        </>
    );
}

/**
 * Operational dashboard body: filters + metrics from composition or preset shortcut.
 * @param {{ onMetricsStateChange?: (state: { loading: boolean, hasMetrics: boolean }) => void }} props
 */
export default function OperationalDashboardPanel({ onMetricsStateChange }) {
    const { user } = useAuth();
    const { selectedCustomerId, isManagingOwnData, isTransportCompany } = useCustomerSelector();
    const defaultRange = useMemo(() => getDefaultDeliveryDateRange(), []);
    const [startDate, setStartDate] = useState(defaultRange.startDate);
    const [endDate, setEndDate] = useState(defaultRange.endDate);
    const [rankingLimit, setRankingLimit] = useState(operationalMetricService.DEFAULT_RANKING_LIMIT);
    const [filterError, setFilterError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState(null);
    const [resolvedKeys, setResolvedKeys] = useState([]);

    const catalogKeys = useMemo(
        () => filterCatalogByRoles(user?.roles || []),
        [user?.roles],
    );

    const customerIdForApi = useMemo(
        () =>
            resolveDashboardCustomerId({
                isTransportCompany,
                selectedCustomerId,
                isManagingOwnData,
            }),
        [isTransportCompany, selectedCustomerId, isManagingOwnData],
    );

    const shouldLoad = canLoadOperationalDashboard({ isTransportCompany, selectedCustomerId })
        && catalogKeys.length > 0;

    const showRankingLimit = resolvedKeys.includes('billing.customer_revenue_ranking')
        || catalogKeys.includes('billing.customer_revenue_ranking');

    useEffect(() => {
        const validation = validateDeliveryDateRange(startDate, endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
            return;
        }
        try {
            if (showRankingLimit) {
                operationalMetricService.normalizeRankingLimit(rankingLimit);
            }
            setFilterError(null);
        } catch (err) {
            setFilterError(err.message);
        }
    }, [startDate, endDate, rankingLimit, showRankingLimit]);

    const loadDashboard = useCallback(async () => {
        if (!shouldLoad || filterError) {
            setResolvedKeys([]);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const types = await operationalMetricService.fetchOperationalMetricTypes();
            const keys = intersectWithApiTypes(catalogKeys, types);
            setResolvedKeys(keys);
            if (!keys.length) {
                setDashboard(null);
                return;
            }

            const includeRanking = keys.includes('billing.customer_revenue_ranking');
            const params = {
                customerId: customerIdForApi,
                startDate,
                endDate,
                rankingLimit: includeRanking ? rankingLimit : undefined,
            };

            const result = isTransportCompanyDashboardPreset(keys)
                ? await operationalMetricService.fetchOperationalDashboard(params)
                : await operationalMetricService.fetchOperationalMetrics({ ...params, keys });
            setDashboard(result);
        } catch (err) {
            const status = err?.status;
            if (status === 403 || status === 404) {
                setDashboard(null);
                setResolvedKeys([]);
                setLoadError(null);
            } else {
                setDashboard(null);
                setResolvedKeys([]);
                setLoadError(err?.message || 'Não foi possível carregar o painel operacional.');
            }
        } finally {
            setLoading(false);
        }
    }, [
        shouldLoad,
        filterError,
        catalogKeys,
        customerIdForApi,
        startDate,
        endDate,
        rankingLimit,
    ]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const sections = useMemo(
        () => groupMetricsByDashboardSection(dashboard?.metrics || []),
        [dashboard?.metrics],
    );

    const hasMetrics = (dashboard?.metrics?.length || 0) > 0;

    useEffect(() => {
        onMetricsStateChange?.({
            loading: loading && shouldLoad,
            hasMetrics: shouldLoad && hasMetrics,
        });
    }, [loading, hasMetrics, shouldLoad, onMetricsStateChange]);

    if (!catalogKeys.length) {
        return null;
    }

    return (
        <PanelStack>
            <FiltersContainer as="div">
                <FilterGroup>
                    <label htmlFor="dashboard-start-date">Data inicial</label>
                    <input
                        id="dashboard-start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </FilterGroup>
                <FilterGroup>
                    <label htmlFor="dashboard-end-date">Data final</label>
                    <input
                        id="dashboard-end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </FilterGroup>
                {showRankingLimit ? (
                    <FilterGroup>
                        <label htmlFor="dashboard-ranking-limit">Clientes no ranking</label>
                        <input
                            id="dashboard-ranking-limit"
                            type="number"
                            min={1}
                            max={30}
                            value={rankingLimit}
                            onChange={(e) => setRankingLimit(Number(e.target.value))}
                        />
                    </FilterGroup>
                ) : null}
            </FiltersContainer>
            {filterError ? <FilterError role="alert">{filterError}</FilterError> : null}
            {loadError ? <LoadError role="alert">{loadError}</LoadError> : null}

            {sections.map((section) => (
                <CollapsibleSection
                    key={section.id}
                    id={`dashboard-section-${section.id}`}
                    title={section.title}
                >
                    <MetricsLayout
                        layout={section}
                        loading={loading}
                        kpiLabel={section.kpiLabel}
                    />
                </CollapsibleSection>
            ))}
        </PanelStack>
    );
}
